import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Icon, ButtonGroup, Dropdown } from 'semantic-ui-react';
import { format } from 'date-fns';
import { formatDollars, formatNumber, offtakeBidDecimals } from '~/utils';
import ActionsButton from './Actions/ActionsButton';
import ActionsDropdownMenu from './Actions/ActionsDropdownMenu';
import PortfolioProjectsBidTable from './PortfolioProjectsBidTable';
import RfiResponseList from '../../requestForInformation/developer/RfiResponseList';
import { InvestorActivityChecklistSingle } from './InvestorActivityChecklist';
import { InvestorActivityChecklist as InvestorActivityChecklistType } from '~/types/portfolio/PortfolioDealSummary';
import { usePermissions } from '~/requests/permissions/usePermissions';

import '../styles.scss';

interface PortfolioBidRowProps {
    portfolio: any;
    investorBidData: any;
    index: number;
    setAcceptInvestorId: (id: string) => void;
    setDeclineInvestorId: (id: string) => void;
    rfiData: Array<{
        name: string;
        description: string;
        content: string | null;
        snippet: string;
        updatedAt?: string | null;
    }>;
    activity?: InvestorActivityChecklistType | null;
    hasSummaryDoc?: boolean;
}

const PortfolioBidRow: React.FC<PortfolioBidRowProps> = ({
    portfolio,
    investorBidData,
    index,
    setAcceptInvestorId,
    setDeclineInvestorId,
    rfiData,
    activity,
    hasSummaryDoc
}) => {
    const { permissions } = usePermissions();
    const [isExpanded, setIsExpanded] = useState(false);

    const projectsArray = investorBidData.projects ? Object.values(investorBidData.projects) : [];
    let bidCount = 0;

    const handleRowClick = () => {
        setIsExpanded(!isExpanded);
    };

    // console.log('portfolio bid row data:', portfolio);

    const calculateBidValue = (unit: 'WDC' | 'KWH' | 'TOTAL') => {
        let bidTotal = 0;
        let totalSizeKwdc = 0;
        let count = 0;
        let totalWeightedUnit = 0;
        let totalWeight = 0;

        projectsArray.forEach((project: any) => {
            const bid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            if (bid && bid.amount) {
                const projectSizeKwdc = project.projectArrays.reduce((sum: number, array: any) => sum + (array.sizeKwdc || 0), 0);
                totalSizeKwdc += projectSizeKwdc;
                count += 1;
                if (bid.unit === 'WDC') {
                    bidTotal += bid.amount * projectSizeKwdc * 1000;
                    totalWeightedUnit += bid.amount * projectSizeKwdc;
                    totalWeight += projectSizeKwdc;
                } else if (bid.unit === 'TOTAL') {
                    bidTotal += bid.amount;
                } else {
                    totalWeightedUnit += bid.amount * projectSizeKwdc;
                    totalWeight += projectSizeKwdc;
                }
            }
        });
        bidCount = count;
        const allDeclined = projectsArray.length > 0 && projectsArray.every((project: any) =>
            project?.status === 'DECLINED_TO_BID' || project?.status === 'BID_REJECTED'
        );
        if (count === 0) return allDeclined ? <i>Declined</i> : '-';
        if (count > 0 && count < projectsArray.length) return <i>Partial</i>;
        if (unit === 'TOTAL') {
            return formatDollars(bidTotal);
        } else if (unit === 'WDC') {
            return '$' + formatNumber(bidTotal / totalSizeKwdc / 1000, 3);
        } else {
            if (totalWeight === 0) return '-';
            return '$' + formatNumber(totalWeightedUnit / totalWeight, offtakeBidDecimals(portfolio.projects[0]?.contractType));
        }
    };

    const getLastUpdated = () => {
        let lastUpdatedDate: Date | null = null;
        const consider = (raw: any) => {
            if (!raw) return;
            const d = new Date(raw);
            if (!isNaN(d.getTime()) && (!lastUpdatedDate || d > lastUpdatedDate)) {
                lastUpdatedDate = d;
            }
        };

        projectsArray.forEach((project: any) => {
            const pendingBid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
            if (pendingBid) {
                consider(pendingBid.createdAt);
            } else if (project?.status === 'DECLINED_TO_BID' || project?.status === 'BID_REJECTED') {
                consider(project?.updatedAt);
            }
        });

        if (lastUpdatedDate) {
            return format(lastUpdatedDate, 'MMM d, yyyy');
        }
        return '-';
    };


    return (
        <React.Fragment>
            <Table.Row className="bid-row" style={{ cursor: 'pointer' }} onClick={handleRowClick}>
                <Table.Cell>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name='dropdown' style={{ marginRight: '8px' }} className={'row-dropdown' + (isExpanded ? ' open' : '')} />
                        <div className="investor-info">
                            <span style={{ fontWeight: 'bold' }}>
                                {investorBidData.investor?.company?.name}
                            </span>
                            {permissions?.isAdmin && investorBidData.investor?.company?.id ? (
                                <Link
                                    to={`/company-profile/${investorBidData.investor.company.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Open company profile in new tab"
                                    style={{ marginLeft: 8, fontSize: 12, textDecoration: 'none' }}
                                >
                                    <Icon name="external" />
                                </Link>
                            ) : null}
                        </div>
                    </div>
                </Table.Cell>
                {portfolio.projects[0].pricingType === 'OFFTAKE' ? (
                    <Table.Cell textAlign="right">
                        {calculateBidValue('KWH')}
                    </Table.Cell>
                ) : null}
                {portfolio.projects[0].pricingType === 'ACQUISITION' || portfolio.projects[0].pricingType === 'DEV_FEE' ? (
                    <>
                        <Table.Cell textAlign="right">
                            {calculateBidValue('WDC')}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                            {calculateBidValue('TOTAL')}
                        </Table.Cell>
                    </>
                ) : null}
                <Table.Cell textAlign="right">
                    {getLastUpdated()}
                </Table.Cell>
                <Table.Cell textAlign="right">
                    <div className="portfolio-actions">
                        <ButtonGroup primary size="small" className='button-group'>
                            <ActionsButton
                                investor={investorBidData.investor}
                                awaitingBid={bidCount < projectsArray.length}
                                setAcceptInvestorId={setAcceptInvestorId}
                            />
                            <Dropdown
                                className='button icon small'
                                style={{ '--color-primary': 'var(--color-secondary)' }}
                                trigger={<></>}
                                portalId='root'
                            >
                                <Dropdown.Menu>
                                    <ActionsDropdownMenu
                                        investor={investorBidData.investor}
                                        awaitingBid={bidCount < projectsArray.length}
                                        setDeclineInvestorId={setDeclineInvestorId}
                                    />
                                </Dropdown.Menu>
                            </Dropdown>
                        </ButtonGroup>
                    </div>
                </Table.Cell>
            </Table.Row>
            {isExpanded && (
                <Table.Row className="bid-row-accordion" style={{ backgroundColor: '#f8f9fa' }}>
                    <Table.Cell colSpan={5} style={{ padding: '0 0 20px' }}>
                        <div className="accordion-content">
                            <h5>Project Bids</h5>
                            <PortfolioProjectsBidTable
                                projects={projectsArray}
                                investorName={investorBidData.investor?.company?.name || 'Unknown Investor'}
                                investorId={investorBidData.investor?.id || ''}
                            />

                            {rfiData && rfiData.length > 0 && (
                                <div style={{ marginTop: '30px' }}>
                                    <h5>Supporting Information</h5>
                                    <RfiResponseList
                                        rfiResponses={rfiData}
                                        investorName={investorBidData.investor?.company?.name || 'Unknown Investor'}
                                        type="portfolio"
                                        tableView={true}
                                    />
                                </div>
                            )}

                            {(permissions?.isAdmin || permissions?.isDeveloper) && (
                                <div style={{ marginTop: '30px' }}>
                                    <h5>Activity</h5>
                                    <InvestorActivityChecklistSingle
                                        activity={activity}
                                        showDates={permissions?.isAdmin}
                                        hasSummaryDoc={hasSummaryDoc}
                                    />
                                </div>
                            )}

                        </div>
                    </Table.Cell>
                </Table.Row>
            )}
        </React.Fragment>
    );
};

export default PortfolioBidRow;
